import { MaterialCategoryId } from '@core/constants/Material';
import {
    BorderShapeId, ExtensionShapeId, LegsShapeId, ShapeId, TrayShapeId,
} from '@core/constants/Shape';
import { IFormFieldKey, IFormFields } from '../interfaces/Form';
import {
    IFormFieldExclusion, IReasons, IReason, IDisabledOption,
} from '../interfaces/Interfiled';

// eslint-disable-next-line unicorn/prefer-spread
const addReason = (reason:IReason, reasons:IReasons | undefined) => (reasons !== undefined ? Array.from(new Set([...reasons, reason])) : [reason]);

export const getLegsMaterialFormFieldExclusion = (fields:IFormFields, options:MaterialCategoryId[]) => {
    const disabledOptions:IDisabledOption = {};
    const legsShape = fields?.legsShape?.value;

    const metalLegs = new Set([LegsShapeId.LegsUShapedStrengthened, LegsShapeId.LegsHairPin, LegsShapeId.LegsRightCrossPiece, LegsShapeId.LegsTrapezeCentral, LegsShapeId.LegsVShapedCentral]);
    const woodLegs = new Set([LegsShapeId.LegsScandinavian, LegsShapeId.LegsMonastery]);
    const metalOrWoodLegs = new Set([LegsShapeId.LegsMikado, LegsShapeId.LegsUShaped, LegsShapeId.LegsCross, LegsShapeId.LegsTrapeze, LegsShapeId.LegsTrapezeInverted, LegsShapeId.LegsRoundLegs, LegsShapeId.LegsSquareCrossPiece, LegsShapeId.LegsVShaped, LegsShapeId.LegsTrestleCross]);

    if (metalLegs.has(legsShape as LegsShapeId)) {
        options.forEach((materialCategoryId) => {
            if (![MaterialCategoryId.Metal, MaterialCategoryId.Other].includes(materialCategoryId)) {
                disabledOptions[materialCategoryId] = addReason(IReason.LegsMaterialConflictLegsShape, disabledOptions[materialCategoryId]);
            }
        });
    }

    if (woodLegs.has(legsShape as LegsShapeId)) {
        options.forEach((materialCategoryId) => {
            if (![MaterialCategoryId.Wood, MaterialCategoryId.Other].includes(materialCategoryId)) {
                disabledOptions[materialCategoryId] = addReason(IReason.LegsMaterialConflictLegsShape, disabledOptions[materialCategoryId]);
            }
        });
    }

    if (metalOrWoodLegs.has(legsShape as LegsShapeId)) {
        options.forEach((materialCategoryId) => {
            if (![
                MaterialCategoryId.Wood,
                MaterialCategoryId.Metal,
                MaterialCategoryId.Other,
            ].includes(materialCategoryId)) {
                disabledOptions[materialCategoryId] = addReason(IReason.LegsMaterialConflictLegsShape, disabledOptions[materialCategoryId]);
            }
        });
    }

    return {
        disabled: false,
        disabledOptions,
    } as IFormFieldExclusion;
};

//  set tray material dependency based on border (disabled all except wood, if LiveEdge or AirplaneWings selected)
//  set tray material dependency based on extension (disable riverTable and metal, if OffsetExtension selected)
//  set tray material dependency based on extension (disabled all except wood, if CentralExtension selected)
export const getTrayMaterialFormFieldExclusion = (fields: IFormFields, options: MaterialCategoryId[]) => {
    const disabledOptions:IDisabledOption = {};

    if (fields?.border?.value === BorderShapeId.LiveEdge) {
        options.forEach((materialCategoryId) => {
            if (![
                MaterialCategoryId.Wood,
                MaterialCategoryId.RiverTable,
                MaterialCategoryId.Other,
            ].includes(materialCategoryId)) {
                disabledOptions[materialCategoryId] = addReason(IReason.TrayMaterialConflictWithBorder, disabledOptions[materialCategoryId]);
            }
        });
    }
    if (fields?.border?.value === BorderShapeId.AirplaneWings) {
        options.forEach((materialCategoryId) => {
            if (![
                MaterialCategoryId.Wood,
                MaterialCategoryId.Other,
            ].includes(materialCategoryId)) {
                disabledOptions[materialCategoryId] = addReason(IReason.TrayMaterialConflictWithBorder, disabledOptions[materialCategoryId]);
            }
        });
    }
    if (fields?.extension?.shape.value === ExtensionShapeId.OffsetExtension) {
        options.forEach((materialCategoryId) => {
            if ([
                MaterialCategoryId.RiverTable,
                MaterialCategoryId.Metal,
            ].includes(materialCategoryId)) {
                disabledOptions[materialCategoryId] = addReason(IReason.TrayMaterialConflictWithOffsetExtension, disabledOptions[materialCategoryId]);
            }
        });
    }
    if (fields?.extension?.shape.value === ExtensionShapeId.CentralExtension) {
        options.forEach((materialCategoryId) => {
            if (![
                MaterialCategoryId.Wood,
                MaterialCategoryId.Other,
            ].includes(materialCategoryId)) {
                disabledOptions[materialCategoryId] = addReason(IReason.TrayMaterialConflictWithCentralExtension, disabledOptions[materialCategoryId]);
            }
        });
    }

    return {
        disabled: false,
        disabledOptions,
    } as IFormFieldExclusion;
};

//  set border dependency based on tray material (LiveEdge and AirplaneWings disabled, if tray material value is NOT wood or river or other)
export const getBorderFormFieldExclusion = (fields:IFormFields, options:ShapeId[]) => {
    const disabledOptions:IDisabledOption = {};
    const materialCategoryId = fields?.trayMaterial?.materialCategory?.value;
    if (materialCategoryId !== undefined) {
        if (![MaterialCategoryId.Wood, MaterialCategoryId.RiverTable, MaterialCategoryId.Other].includes(materialCategoryId) && options.includes(BorderShapeId.LiveEdge)) disabledOptions[BorderShapeId.LiveEdge] = addReason(IReason.BorderConflictWithLiveEdgeMaterial, []);
        if (![MaterialCategoryId.Wood, MaterialCategoryId.Other].includes(materialCategoryId) && options.includes(BorderShapeId.AirplaneWings)) disabledOptions[BorderShapeId.AirplaneWings] = addReason(IReason.BorderConflictWithAirplaneWingsMaterial, []);
    }

    return {
        disabled: false,
        disabledOptions,
    } as IFormFieldExclusion;
};

//  set extension dependency based on legs (disable central, if RightCrossPiece, SquareCrossPiece, or TrestleCross selected)
//  set extension dependency based on shape (disable offset, if round or oval selected)
//  set extension dependency based on material (disable all, if riverTable or metal selected)
export const getExtensionFormFieldExclusion = (fields:IFormFields, options:ShapeId[]) => {
    const disabledOptions:IDisabledOption = {};
    const hasCentralOption = options.includes(ExtensionShapeId.CentralExtension);
    const hasOffsetOption = options.includes(ExtensionShapeId.OffsetExtension);
    const hasSideOption = options.includes(ExtensionShapeId.SideExtension);
    const hasButterflyOption = options.includes(ExtensionShapeId.ButterflyExtension);
    const hasReturnLeftOption = options.includes(TrayShapeId.ReturnLeft);
    const hasReturnRightOption = options.includes(TrayShapeId.ReturnRight);
    const materialCategoryId = fields?.trayMaterial?.materialCategory?.value;
    const isTrayMaterialConflict = materialCategoryId !== undefined && [MaterialCategoryId.RiverTable, MaterialCategoryId.Metal].includes(materialCategoryId);

    // shape conflicts
    if (hasCentralOption && fields?.legsShape?.value === LegsShapeId.LegsRightCrossPiece) disabledOptions[ExtensionShapeId.CentralExtension] = [IReason.ExtensionConflictWithRightCrossPieceLegsShape];
    if (hasCentralOption && fields?.legsShape?.value === LegsShapeId.LegsSquareCrossPiece) disabledOptions[ExtensionShapeId.CentralExtension] = [IReason.ExtensionConflictWithSquareCrossPieceLegsShape];
    if (hasCentralOption && fields?.legsShape?.value === LegsShapeId.LegsTrestleCross) disabledOptions[ExtensionShapeId.CentralExtension] = [IReason.ExtensionConflictWithTrestleCrossLegsShape];

    if (hasButterflyOption && fields?.legsShape?.value === LegsShapeId.LegsRightCrossPiece) disabledOptions[ExtensionShapeId.ButterflyExtension] = [IReason.ExtensionConflictWithRightCrossPieceLegsShape];
    if (hasButterflyOption && fields?.legsShape?.value === LegsShapeId.LegsSquareCrossPiece) disabledOptions[ExtensionShapeId.ButterflyExtension] = [IReason.ExtensionConflictWithSquareCrossPieceLegsShape];
    if (hasButterflyOption && fields?.legsShape?.value === LegsShapeId.LegsTrestleCross) disabledOptions[ExtensionShapeId.ButterflyExtension] = [IReason.ExtensionConflictWithTrestleCrossLegsShape];

    if (hasOffsetOption && fields?.trayShape?.value === TrayShapeId.TrayRound) disabledOptions[ExtensionShapeId.OffsetExtension] = [IReason.ExtensionConflictWithRoundTrayShape];
    if (hasOffsetOption && fields?.trayShape?.value === TrayShapeId.TrayOval) disabledOptions[ExtensionShapeId.OffsetExtension] = [IReason.ExtensionConflictWithOvalTrayShape];

    if (hasSideOption && fields?.trayShape?.value === TrayShapeId.TrayRound) disabledOptions[ExtensionShapeId.SideExtension] = [IReason.ExtensionConflictWithRoundTrayShape];
    if (hasSideOption && fields?.trayShape?.value === TrayShapeId.TrayOval) disabledOptions[ExtensionShapeId.SideExtension] = [IReason.ExtensionConflictWithOvalTrayShape];

    if (hasReturnLeftOption && fields?.trayShape?.value === TrayShapeId.TrayRound) disabledOptions[TrayShapeId.ReturnLeft] = [IReason.ExtensionConflictWithRoundTrayShape];
    if (hasReturnLeftOption && fields?.trayShape?.value === TrayShapeId.TrayOval) disabledOptions[TrayShapeId.ReturnLeft] = [IReason.ExtensionConflictWithOvalTrayShape];

    if (hasReturnRightOption && fields?.trayShape?.value === TrayShapeId.TrayRound) disabledOptions[TrayShapeId.ReturnRight] = [IReason.ExtensionConflictWithRoundTrayShape];
    if (hasReturnRightOption && fields?.trayShape?.value === TrayShapeId.TrayOval) disabledOptions[TrayShapeId.ReturnRight] = [IReason.ExtensionConflictWithOvalTrayShape];

    if (hasOffsetOption && fields?.trayShapeDimension?.shape.value === TrayShapeId.TrayRound) disabledOptions[ExtensionShapeId.OffsetExtension] = [IReason.ExtensionConflictWithRoundTrayShape];
    if (hasOffsetOption && fields?.trayShapeDimension?.shape.value === TrayShapeId.TrayOval) disabledOptions[ExtensionShapeId.OffsetExtension] = [IReason.ExtensionConflictWithOvalTrayShape];

    if (hasSideOption && fields?.trayShapeDimension?.shape.value === TrayShapeId.TrayRound) disabledOptions[ExtensionShapeId.SideExtension] = [IReason.ExtensionConflictWithRoundTrayShape];
    if (hasSideOption && fields?.trayShapeDimension?.shape.value === TrayShapeId.TrayOval) disabledOptions[ExtensionShapeId.SideExtension] = [IReason.ExtensionConflictWithOvalTrayShape];

    if (hasReturnLeftOption && fields?.trayShapeDimension?.shape.value === TrayShapeId.TrayRound) disabledOptions[TrayShapeId.ReturnLeft] = [IReason.ExtensionConflictWithRoundTrayShape];
    if (hasReturnLeftOption && fields?.trayShapeDimension?.shape.value === TrayShapeId.TrayOval) disabledOptions[TrayShapeId.ReturnLeft] = [IReason.ExtensionConflictWithOvalTrayShape];

    if (hasReturnRightOption && fields?.trayShapeDimension?.shape.value === TrayShapeId.TrayRound) disabledOptions[TrayShapeId.ReturnRight] = [IReason.ExtensionConflictWithRoundTrayShape];
    if (hasReturnRightOption && fields?.trayShapeDimension?.shape.value === TrayShapeId.TrayOval) disabledOptions[TrayShapeId.ReturnRight] = [IReason.ExtensionConflictWithOvalTrayShape];

    // material conflicts
    if (hasCentralOption && materialCategoryId !== undefined && materialCategoryId !== MaterialCategoryId.Wood && materialCategoryId !== MaterialCategoryId.Other) disabledOptions[ExtensionShapeId.CentralExtension] = [IReason.ExtensionConflictWithTrayMaterial];
    if (hasButterflyOption && materialCategoryId !== undefined && materialCategoryId !== MaterialCategoryId.Wood && materialCategoryId !== MaterialCategoryId.Other) disabledOptions[ExtensionShapeId.ButterflyExtension] = [IReason.ExtensionConflictWithTrayMaterial];

    const hasAllAvailableOptionsDisabled = !options.some((option) => !disabledOptions[option]);

    // all extensions are in conflict
    const isExtensionDisabled = isTrayMaterialConflict || hasAllAvailableOptionsDisabled;

    const result:IFormFieldExclusion = isExtensionDisabled ? {
        disabled : true,
        // eslint-disable-next-line unicorn/prefer-spread
        reasons  : Array.from(new Set([
            ...(isTrayMaterialConflict ? [IReason.ExtensionConflictWithTrayMaterial] : []),
            ...(Object.values(disabledOptions).reduce((acc, reasons) => [...acc, ...reasons], [])),
        ])),
        disabledOptions: {},
    } : {
        disabled: false,
        disabledOptions,
    } as IFormFieldExclusion;

    return result;
};

//  set legs dependency based on extension (disable RightCrossPiece, SquareCrossPiece, or TrestleCross, if centralExtension selected)
export const getLegsShapeFormFieldExclusion = (fields:IFormFields, options:ShapeId[]) => {
    const disabledOptions:IDisabledOption = {};
    const legsMaterialCategory = fields?.legsMaterial?.materialCategory?.value;

    const metalLegs = [LegsShapeId.LegsUShapedStrengthened, LegsShapeId.LegsHairPin, LegsShapeId.LegsRightCrossPiece, LegsShapeId.LegsTrapezeCentral, LegsShapeId.LegsVShapedCentral];
    const woodLegs = [LegsShapeId.LegsScandinavian, LegsShapeId.LegsMonastery];
    const metalOrWoodLegs = [LegsShapeId.LegsMikado, LegsShapeId.LegsUShaped, LegsShapeId.LegsCross, LegsShapeId.LegsTrapeze, LegsShapeId.LegsTrapezeInverted, LegsShapeId.LegsRoundLegs, LegsShapeId.LegsSquareCrossPiece, LegsShapeId.LegsVShaped, LegsShapeId.LegsTrestleCross];

    if ([MaterialCategoryId.Wood, MaterialCategoryId.Glass, MaterialCategoryId.Concrete, MaterialCategoryId.Ceramic, MaterialCategoryId.MarbleAndStone].includes(legsMaterialCategory as MaterialCategoryId)) {
        metalLegs.forEach((materialCategoryId) => {
            disabledOptions[materialCategoryId] = [IReason.LegsShapeConflictLegsMaterial];
        });
    }

    if ([MaterialCategoryId.Metal, MaterialCategoryId.Glass, MaterialCategoryId.Concrete, MaterialCategoryId.Ceramic, MaterialCategoryId.MarbleAndStone].includes(legsMaterialCategory as MaterialCategoryId)) {
        woodLegs.forEach((legsShape) => {
            disabledOptions[legsShape] = [IReason.LegsShapeConflictLegsMaterial];
        });
    }

    if ([MaterialCategoryId.Glass, MaterialCategoryId.Concrete, MaterialCategoryId.Ceramic, MaterialCategoryId.MarbleAndStone].includes(legsMaterialCategory as MaterialCategoryId)) {
        metalOrWoodLegs.forEach((legsShape) => {
            disabledOptions[legsShape] = [IReason.LegsShapeConflictLegsMaterial];
        });
    }

    const returnTrayShape = new Set([TrayShapeId.ReturnRight, TrayShapeId.ReturnLeft]);
    if (
        returnTrayShape.has(fields?.trayShape?.value as TrayShapeId)
        || returnTrayShape.has(fields?.trayShapeDimension?.shape?.value as TrayShapeId)
    ) {
        [LegsShapeId.LegsMikado, LegsShapeId.LegsTrapezeCentral, LegsShapeId.LegsVShaped, LegsShapeId.LegsVShapedCentral]
            .forEach((id) => {
                if (options.includes(id)) disabledOptions[id] = [IReason.LegsShapeConflictWithTrayShape];
            });
    }

    if (fields?.extension?.shape?.value === ExtensionShapeId.CentralExtension) {
        [LegsShapeId.LegsRightCrossPiece, LegsShapeId.LegsSquareCrossPiece, LegsShapeId.LegsTrestleCross]
            .forEach((id) => {
                if (options.includes(id)) disabledOptions[id] = [IReason.LegsShapeConflictWithExtension];
            });
    }

    return {
        disabled: false,
        disabledOptions,
    } as IFormFieldExclusion;
};

//  set shape tray dependency based on extension (disable round and oval, if OffsetExtension selected)
export const getTrayShapeFormFieldExclusion = (fields:IFormFields, options:ShapeId[]) => {
    const disabledOptions:IDisabledOption = {};

    const centralLegsShapes = new Set([LegsShapeId.LegsMikado, LegsShapeId.LegsTrapezeCentral, LegsShapeId.LegsVShaped, LegsShapeId.LegsVShapedCentral]);
    if (centralLegsShapes.has(fields?.legsShape?.value as LegsShapeId)) {
        [TrayShapeId.ReturnRight, TrayShapeId.ReturnLeft]
            .forEach((id) => {
                if (options.includes(id)) disabledOptions[id] = [IReason.TrayShapeConflictWithLegsShape];
            });
    }

    if (fields?.extension?.shape?.value === ExtensionShapeId.OffsetExtension) {
        [TrayShapeId.TrayRound, TrayShapeId.TrayOval]
            .forEach((id) => {
                if (options.includes(id)) disabledOptions[id] = [IReason.TrayShapeConflictWithExtension];
            });
    }

    return {
        disabled: false,
        disabledOptions,
    } as IFormFieldExclusion;
};

export const formFieldKeysToExclusions = {
    [IFormFieldKey.TrayMaterial]       : getTrayMaterialFormFieldExclusion,
    [IFormFieldKey.LegsMaterial]       : getLegsMaterialFormFieldExclusion,
    [IFormFieldKey.Material]           : getTrayMaterialFormFieldExclusion,
    [IFormFieldKey.Border]             : getBorderFormFieldExclusion,
    [IFormFieldKey.Extension]          : getExtensionFormFieldExclusion,
    [IFormFieldKey.LegsShape]          : getLegsShapeFormFieldExclusion,
    [IFormFieldKey.TrayShape]          : getTrayShapeFormFieldExclusion,
    [IFormFieldKey.Shape]              : getTrayShapeFormFieldExclusion,
    [IFormFieldKey.TrayShapeDimension] : getTrayShapeFormFieldExclusion,
    [IFormFieldKey.ShapeDimension]     : getTrayShapeFormFieldExclusion,
};
